import { PDFDocument, rgb } from "pdf-lib";
import templateUrl from './template.pdf';
import { drawLogos } from "@/reports/shared_utils/drawLogos";
import { collectUnsupportedHighlights, drawUnsupportedHighlights } from "@/reports/shared_utils/highlightUnsupported";
import { sanitizeForPdf } from "@/reports/shared_utils/sanitizeForPdf";

// Maps each schema key to the representative PDF field name used for
// highlight positioning in the preview pane.
export const SCHEMA_TO_PDF_FIELD = {
  date: "date", studentName: "studentName1", oen: "oen1", grade: "grade1",
  gradeInSeptember: "gradeInSeptember", board: "board", school: "school",
  address1: "address1", address2: "address2", principal: "principal",
  telephone: "telephone", teacher: "teacher",
  daysAbsent: "daysAbsent", totalDaysAbsent: "totalDaysAbsent",
  timesLate: "timesLate", totalTimesLate: "totalTimesLate",
  responsibility1: "responsibility1", responsibility2: "responsibility2",
  organization1: "organization1", organization2: "organization2",
  independentWork1: "independentWork1", independentWork2: "independentWork2",
  collaboration1: "collaboration1", collaboration2: "collaboration2",
  initiative1: "initiative1", initiative2: "initiative2",
  selfRegulation1: "selfRegulation1", selfRegulation2: "selfRegulation2",
  strengthsNextStepsLSWH: "strengthsNextStepsForImprovementLSWH",
  languagePlan: "languageESLELD",
  languageGrade1: "languageReport1Mark", languageMedian1: "languageReport1Median",
  languageGrade2: "languageReport2Mark", languageMedian2: "languageReport2Median",
  languageComments: "languageComments",
  nativeLanguage: "nativeLanguage", nativeLanguagePlan: "nativeLanguageESLELD",
  nativeLanguageGrade1: "nativeLanguageReport1Mark", nativeLanguageMedian1: "nativeLanguageReport1Median",
  nativeLanguageGrade2: "nativeLanguageReport2Mark", nativeLanguageMedian2: "nativeLanguageReport2Median",
  nativeLanguageComments: "nativeLanguageComments",
  frenchPlan: "frenchNA",
  frenchListeningPlan: "frenchListeningESLELD",
  frenchListeningGrade1: "frenchListeningReport1Mark", frenchListeningMedian1: "frenchListeningReport1Median",
  frenchListeningGrade2: "frenchListeningReport2Mark", frenchListeningMedian2: "frenchListeningReport2Median",
  frenchSpeakingPlan: "frenchSpeakingESLELD",
  frenchSpeakingGrade1: "frenchSpeakingReport1Mark", frenchSpeakingMedian1: "frenchSpeakingReport1Median",
  frenchSpeakingGrade2: "frenchSpeakingReport2Mark", frenchSpeakingMedian2: "frenchSpeakingReport2Median",
  frenchReadingPlan: "frenchReadingESLELD",
  frenchReadingGrade1: "frenchReadingReport1Mark", frenchReadingMedian1: "frenchReadingReport1Median",
  frenchReadingGrade2: "frenchReadingReport2Mark", frenchReadingMedian2: "frenchReadingReport2Median",
  frenchWritingPlan: "frenchWritingESLELD",
  frenchWritingGrade1: "frenchWritingReport1Mark", frenchWritingMedian1: "frenchWritingReport1Median",
  frenchWritingGrade2: "frenchWritingReport2Mark", frenchWritingMedian2: "frenchWritingReport2Median",
  frenchComments: "frenchComments",
  mathematicsPlan: "mathematicsESLELD",
  mathematicsGrade1: "mathematicsReport1Mark", mathematicsMedian1: "mathematicsReport1Median",
  mathematicsGrade2: "mathematicsReport2Mark", mathematicsMedian2: "mathematicsReport2Median",
  mathematicsComments: "mathematicsComments",
  scienceAndTechnologyPlan: "scienceAndTechnologyESLELD",
  scienceAndTechnologyGrade1: "scienceAndTechnologyReport1Mark", scienceAndTechnologyMedian1: "scienceAndTechnologyReport1Median",
  scienceAndTechnologyGrade2: "scienceAndTechnologyReport2Mark", scienceAndTechnologyMedian2: "scienceAndTechnologyReport2Median",
  scienceAndTechnologyComments: "scienceAndTechnologyComments",
  historyPlan: "historyESLELD",
  historyGrade1: "historyReport1Mark", historyMedian1: "historyReport1Median",
  historyGrade2: "historyReport2Mark", historyMedian2: "historyReport2Median",
  historyComments: "historyComments",
  geographyPlan: "geographyESLELD",
  geographyGrade1: "geographyReport1Mark", geographyMedian1: "geographyReport1Median",
  geographyGrade2: "geographyReport2Mark", geographyMedian2: "geographyReport2Median",
  geographyComments: "geographyComments",
  healthEducationPlan: "healthEducationESLELD",
  healthEducationGrade1: "healthEducationReport1Mark", healthEducationMedian1: "healthEducationReport1Median",
  healthEducationGrade2: "healthEducationReport2Mark", healthEducationMedian2: "healthEducationReport2Median",
  physicalEducationPlan: "physicalEducationESLELD",
  physicalEducationGrade1: "physicalEducationReport1Mark", physicalEducationMedian1: "physicalEducationReport1Median",
  physicalEducationGrade2: "physicalEducationReport2Mark", physicalEducationMedian2: "physicalEducationReport2Median",
  healthAndPhysicalEducationComments: "healthAndPhysicalEducationComments",
  artsHeader: "artsHeader",
  arts1: "arts1", arts1Plan: "arts1ESLELD",
  arts1Grade1: "arts1Report1Mark", arts1Median1: "arts1Report1Median",
  arts1Grade2: "arts1Report2Mark", arts1Median2: "arts1Report2Median",
  arts2: "arts2", arts2Plan: "arts2ESLELD",
  arts2Grade1: "arts2Report1Mark", arts2Median1: "arts2Report1Median",
  arts2Grade2: "arts2Report2Mark", arts2Median2: "arts2Report2Median",
  arts3: "arts3", arts3Plan: "arts3ESLELD",
  arts3Grade1: "arts3Report1Mark", arts3Median1: "arts3Report1Median",
  arts3Grade2: "arts3Report2Mark", arts3Median2: "arts3Report2Median",
  arts4: "arts4", arts4Plan: "arts4ESLELD",
  arts4Grade1: "arts4Report1Mark", arts4Median1: "arts4Report1Median",
  arts4Grade2: "arts4Report2Mark", arts4Median2: "arts4Report2Median",
  artsComments: "artsComments",
  optionalStudies: "optionalStudies", optionalStudiesPlan: "optionalStudiesESLELD",
  optionalStudiesGrade1: "optionalStudiesReport1Mark",
  optionalStudiesGrade2: "optionalStudiesReport2Mark",
  optionalStudiesComments: "optionalStudiesComments",
}

// PDF page index (0-based) for each schema field (4 pages).
export const PAGE_INDEX_FOR_SCHEMA_FIELD = {
  date: 0, studentName: 0, oen: 0, grade: 0, gradeInSeptember: 0, teacher: 0,
  board: 0, school: 0, address1: 0, address2: 0, principal: 0, telephone: 0,
  daysAbsent: 0, totalDaysAbsent: 0, timesLate: 0, totalTimesLate: 0,
  responsibility1: 0, responsibility2: 0, organization1: 0, organization2: 0,
  independentWork1: 0, independentWork2: 0, collaboration1: 0, collaboration2: 0,
  initiative1: 0, initiative2: 0, selfRegulation1: 0, selfRegulation2: 0,
  strengthsNextStepsLSWH: 0,
  languagePlan: 1, languageGrade1: 1, languageMedian1: 1, languageGrade2: 1, languageMedian2: 1, languageComments: 1,
  nativeLanguage: 1, nativeLanguagePlan: 1,
  nativeLanguageGrade1: 1, nativeLanguageMedian1: 1, nativeLanguageGrade2: 1, nativeLanguageMedian2: 1, nativeLanguageComments: 1,
  frenchPlan: 1,
  frenchListeningPlan: 1, frenchListeningGrade1: 1, frenchListeningMedian1: 1, frenchListeningGrade2: 1, frenchListeningMedian2: 1,
  frenchSpeakingPlan: 1, frenchSpeakingGrade1: 1, frenchSpeakingMedian1: 1, frenchSpeakingGrade2: 1, frenchSpeakingMedian2: 1,
  frenchReadingPlan: 1, frenchReadingGrade1: 1, frenchReadingMedian1: 1, frenchReadingGrade2: 1, frenchReadingMedian2: 1,
  frenchWritingPlan: 1, frenchWritingGrade1: 1, frenchWritingMedian1: 1, frenchWritingGrade2: 1, frenchWritingMedian2: 1,
  frenchComments: 1,
  mathematicsPlan: 1, mathematicsGrade1: 1, mathematicsMedian1: 1, mathematicsGrade2: 1, mathematicsMedian2: 1, mathematicsComments: 1,
  scienceAndTechnologyPlan: 1,
  scienceAndTechnologyGrade1: 1, scienceAndTechnologyMedian1: 1,
  scienceAndTechnologyGrade2: 1, scienceAndTechnologyMedian2: 1, scienceAndTechnologyComments: 1,
  historyPlan: 2, historyGrade1: 2, historyMedian1: 2, historyGrade2: 2, historyMedian2: 2, historyComments: 2,
  geographyPlan: 2, geographyGrade1: 2, geographyMedian1: 2, geographyGrade2: 2, geographyMedian2: 2, geographyComments: 2,
  healthEducationPlan: 2, healthEducationGrade1: 2, healthEducationMedian1: 2, healthEducationGrade2: 2, healthEducationMedian2: 2,
  physicalEducationPlan: 2, physicalEducationGrade1: 2, physicalEducationMedian1: 2, physicalEducationGrade2: 2, physicalEducationMedian2: 2,
  healthAndPhysicalEducationComments: 2,
  artsHeader: 2,
  arts1: 2, arts1Plan: 2, arts1Grade1: 2, arts1Median1: 2, arts1Grade2: 2, arts1Median2: 2,
  arts2: 2, arts2Plan: 2, arts2Grade1: 2, arts2Median1: 2, arts2Grade2: 2, arts2Median2: 2,
  arts3: 2, arts3Plan: 2, arts3Grade1: 2, arts3Median1: 2, arts3Grade2: 2, arts3Median2: 2,
  arts4: 2, arts4Plan: 2, arts4Grade1: 2, arts4Median1: 2, arts4Grade2: 2, arts4Median2: 2,
  artsComments: 2,
  optionalStudies: 2, optionalStudiesPlan: 2, optionalStudiesGrade1: 2, optionalStudiesGrade2: 2, optionalStudiesComments: 2,
}

// One representative PDF field per step - used to compute the scroll
// position in the preview pane when the user changes steps.
export const STEP_ANCHOR_FIELDS = {
  1: "studentName1",                  2: "board",                        3: "responsibility1",
  4: "languageReport1Mark",           5: "frenchListeningReport1Mark",   6: "mathematicsReport1Mark",
  7: "scienceAndTechnologyReport1Mark", 8: "historyReport1Mark",
  9: "healthEducationReport1Mark",    10: "artsHeader",                  11: "studentName4",
}

const val = (v) => sanitizeForPdf(v);

const fill = async (pdfFormObject, highlightRect = null, logos = []) => {
  const existingPdfBytes = await fetch(templateUrl).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const pdfForm = pdfDoc.getForm();

  var field;

  // ── PAGE 1: Header / Attendance / Learning Skills ─────────────────────────

  field = pdfForm.getField("date");
  field.setText(val(pdfFormObject["date"]));
  field.setFontSize(8);

  field = pdfForm.getField("studentName1");
  field.setText(val(pdfFormObject["studentName"]));
  field.setFontSize(8);

  field = pdfForm.getField("oen1");
  field.setText(val(pdfFormObject["oen"]));
  field.setFontSize(8);

  field = pdfForm.getField("grade1");
  field.setText(val(pdfFormObject["grade"]));
  field.setFontSize(8);

  field = pdfForm.getField("teacher");
  field.setText(val(pdfFormObject["teacher"]));
  field.setFontSize(8);

  field = pdfForm.getField("board");
  field.setText(val(pdfFormObject["board"]));
  field.setFontSize(8);

  field = pdfForm.getField("school");
  field.setText(val(pdfFormObject["school"]));
  field.setFontSize(8);

  field = pdfForm.getField("address1");
  field.setText(val(pdfFormObject["address1"]));
  field.setFontSize(8);

  field = pdfForm.getField("address2");
  field.setText(val(pdfFormObject["address2"]));
  field.setFontSize(8);

  field = pdfForm.getField("principal");
  field.setText(val(pdfFormObject["principal"]));
  field.setFontSize(8);

  field = pdfForm.getField("telephone");
  field.setText(val(pdfFormObject["telephone"]));
  field.setFontSize(8);

  field = pdfForm.getField("daysAbsent");
  field.setText(val(pdfFormObject["daysAbsent"]));
  field.setFontSize(8);

  field = pdfForm.getField("totalDaysAbsent");
  field.setText(val(pdfFormObject["totalDaysAbsent"]));
  field.setFontSize(8);

  field = pdfForm.getField("timesLate");
  field.setText(val(pdfFormObject["timesLate"]));
  field.setFontSize(8);

  field = pdfForm.getField("totalTimesLate");
  field.setText(val(pdfFormObject["totalTimesLate"]));
  field.setFontSize(8);

  field = pdfForm.getField("gradeInSeptember");
  field.setText(val(pdfFormObject["gradeInSeptember"]));
  field.setFontSize(12);

  // Learning skills may be text fields or radio groups depending on the PDF template.
  // Try setText first; fall back to radio group select if the field type doesn't support it.
  const setSkill = (name, value) => {
    const v = val(value);
    try {
      const f = pdfForm.getTextField(name);
      f.setText(v);
      f.setFontSize(11);
    } catch {
      const rg = pdfForm.getRadioGroup(name);
      if (v) rg.select(v); else rg.deselect();
    }
  };

  setSkill("responsibility1", pdfFormObject["responsibility1"]);
  setSkill("responsibility2", pdfFormObject["responsibility2"]);
  setSkill("organization1", pdfFormObject["organization1"]);
  setSkill("organization2", pdfFormObject["organization2"]);
  setSkill("independentWork1", pdfFormObject["independentWork1"]);
  setSkill("independentWork2", pdfFormObject["independentWork2"]);
  setSkill("collaboration1", pdfFormObject["collaboration1"]);
  setSkill("collaboration2", pdfFormObject["collaboration2"]);
  setSkill("initiative1", pdfFormObject["initiative1"]);
  setSkill("initiative2", pdfFormObject["initiative2"]);
  setSkill("selfRegulation1", pdfFormObject["selfRegulation1"]);
  setSkill("selfRegulation2", pdfFormObject["selfRegulation2"]);

  field = pdfForm.getField("strengthsNextStepsForImprovementLSWH");
  field.setText(val(pdfFormObject["strengthsNextStepsLSWH"]));
  field.setFontSize(8);
  field.enableMultiline();

  // ── PAGE 2: Language / French / Math / Science ────────────────────────────

  field = pdfForm.getField("studentName2");
  field.setText(val(pdfFormObject["studentName"]));
  field.setFontSize(9);
  field = pdfForm.getField("oen2");
  field.setText(val(pdfFormObject["oen"]));
  field.setFontSize(9);
  field = pdfForm.getField("grade2");
  field.setText(val(pdfFormObject["grade"]));
  field.setFontSize(9);

  // LANGUAGE
  field = pdfForm.getField("languageESLELD");
  pdfFormObject["languagePlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("languageIEP");
  pdfFormObject["languagePlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("languageNA");
  pdfFormObject["languagePlan"] === "NA" ? field.check() : field.uncheck();

  field = pdfForm.getField("languageReport1Mark");
  field.setText(val(pdfFormObject["languageGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("languageReport1Median");
  field.setText(val(pdfFormObject["languageMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("languageReport2Mark");
  field.setText(val(pdfFormObject["languageGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("languageReport2Median");
  field.setText(val(pdfFormObject["languageMedian2"]));
  field.setFontSize(9);

  field = pdfForm.getField("languageComments");
  field.setText(val(pdfFormObject["languageComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // FRENCH
  field = pdfForm.getField("frenchNA");
  pdfFormObject["frenchPlan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchCore");
  pdfFormObject["frenchPlan"] === "Core" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchImmersion");
  pdfFormObject["frenchPlan"] === "Immersion" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchExtended");
  pdfFormObject["frenchPlan"] === "Extended" ? field.check() : field.uncheck();

  field = pdfForm.getField("frenchListeningESLELD");
  pdfFormObject["frenchListeningPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchListeningIEP");
  pdfFormObject["frenchListeningPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchListeningReport1Mark");
  field.setText(val(pdfFormObject["frenchListeningGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchListeningReport1Median");
  field.setText(val(pdfFormObject["frenchListeningMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchListeningReport2Mark");
  field.setText(val(pdfFormObject["frenchListeningGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchListeningReport2Median");
  field.setText(val(pdfFormObject["frenchListeningMedian2"]));
  field.setFontSize(9);

  field = pdfForm.getField("frenchSpeakingESLELD");
  pdfFormObject["frenchSpeakingPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchSpeakingIEP");
  pdfFormObject["frenchSpeakingPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchSpeakingReport1Mark");
  field.setText(val(pdfFormObject["frenchSpeakingGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchSpeakingReport1Median");
  field.setText(val(pdfFormObject["frenchSpeakingMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchSpeakingReport2Mark");
  field.setText(val(pdfFormObject["frenchSpeakingGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchSpeakingReport2Median");
  field.setText(val(pdfFormObject["frenchSpeakingMedian2"]));
  field.setFontSize(9);

  field = pdfForm.getField("frenchReadingESLELD");
  pdfFormObject["frenchReadingPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchReadingIEP");
  pdfFormObject["frenchReadingPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchReadingReport1Mark");
  field.setText(val(pdfFormObject["frenchReadingGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchReadingReport1Median");
  field.setText(val(pdfFormObject["frenchReadingMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchReadingReport2Mark");
  field.setText(val(pdfFormObject["frenchReadingGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchReadingReport2Median");
  field.setText(val(pdfFormObject["frenchReadingMedian2"]));
  field.setFontSize(9);

  field = pdfForm.getField("frenchWritingESLELD");
  pdfFormObject["frenchWritingPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchWritingIEP");
  pdfFormObject["frenchWritingPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchWritingReport1Mark");
  field.setText(val(pdfFormObject["frenchWritingGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchWritingReport1Median");
  field.setText(val(pdfFormObject["frenchWritingMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchWritingReport2Mark");
  field.setText(val(pdfFormObject["frenchWritingGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchWritingReport2Median");
  field.setText(val(pdfFormObject["frenchWritingMedian2"]));
  field.setFontSize(9);

  field = pdfForm.getField("frenchComments");
  field.setText(val(pdfFormObject["frenchComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // NATIVE LANGUAGE
  field = pdfForm.getField("nativeLanguage");
  field.setText(val(pdfFormObject["nativeLanguage"]));
  field.setFontSize(9);
  field = pdfForm.getField("nativeLanguageESLELD");
  pdfFormObject["nativeLanguagePlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("nativeLanguageIEP");
  pdfFormObject["nativeLanguagePlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("nativeLanguageNA");
  pdfFormObject["nativeLanguagePlan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("nativeLanguageReport1Mark");
  field.setText(val(pdfFormObject["nativeLanguageGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("nativeLanguageReport1Median");
  field.setText(val(pdfFormObject["nativeLanguageMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("nativeLanguageReport2Mark");
  field.setText(val(pdfFormObject["nativeLanguageGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("nativeLanguageReport2Median");
  field.setText(val(pdfFormObject["nativeLanguageMedian2"]));
  field.setFontSize(9);
  field = pdfForm.getField("nativeLanguageComments");
  field.setText(val(pdfFormObject["nativeLanguageComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // MATHEMATICS
  field = pdfForm.getField("mathematicsESLELD");
  pdfFormObject["mathematicsPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("mathematicsIEP");
  pdfFormObject["mathematicsPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("mathematicsFrench");
  pdfFormObject["mathematicsPlan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("mathematicsReport1Mark");
  field.setText(val(pdfFormObject["mathematicsGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("mathematicsReport1Median");
  field.setText(val(pdfFormObject["mathematicsMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("mathematicsReport2Mark");
  field.setText(val(pdfFormObject["mathematicsGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("mathematicsReport2Median");
  field.setText(val(pdfFormObject["mathematicsMedian2"]));
  field.setFontSize(9);
  field = pdfForm.getField("mathematicsComments");
  field.setText(val(pdfFormObject["mathematicsComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // SCIENCE AND TECHNOLOGY
  field = pdfForm.getField("scienceAndTechnologyESLELD");
  pdfFormObject["scienceAndTechnologyPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("scienceAndTechnologyIEP");
  pdfFormObject["scienceAndTechnologyPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("scienceAndTechnologyFrench");
  pdfFormObject["scienceAndTechnologyPlan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("scienceAndTechnologyReport1Mark");
  field.setText(val(pdfFormObject["scienceAndTechnologyGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("scienceAndTechnologyReport1Median");
  field.setText(val(pdfFormObject["scienceAndTechnologyMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("scienceAndTechnologyReport2Mark");
  field.setText(val(pdfFormObject["scienceAndTechnologyGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("scienceAndTechnologyReport2Median");
  field.setText(val(pdfFormObject["scienceAndTechnologyMedian2"]));
  field.setFontSize(9);
  field = pdfForm.getField("scienceAndTechnologyComments");
  field.setText(val(pdfFormObject["scienceAndTechnologyComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // ── PAGE 3: History / Geography / Health / PE / Arts ──────────────────────

  field = pdfForm.getField("studentName3");
  field.setText(val(pdfFormObject["studentName"]));
  field.setFontSize(9);
  field = pdfForm.getField("oen3");
  field.setText(val(pdfFormObject["oen"]));
  field.setFontSize(9);
  field = pdfForm.getField("grade3");
  field.setText(val(pdfFormObject["grade"]));
  field.setFontSize(9);

  // HISTORY (fourPlans: ESLELD/IEP/French/NA)
  field = pdfForm.getField("historyESLELD");
  pdfFormObject["historyPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("historyIEP");
  pdfFormObject["historyPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("historyFrench");
  pdfFormObject["historyPlan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("historyNA");
  pdfFormObject["historyPlan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("historyReport1Mark");
  field.setText(val(pdfFormObject["historyGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("historyReport1Median");
  field.setText(val(pdfFormObject["historyMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("historyReport2Mark");
  field.setText(val(pdfFormObject["historyGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("historyReport2Median");
  field.setText(val(pdfFormObject["historyMedian2"]));
  field.setFontSize(9);
  field = pdfForm.getField("historyComments");
  field.setText(val(pdfFormObject["historyComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // GEOGRAPHY (fourPlans: ESLELD/IEP/French/NA)
  field = pdfForm.getField("geographyESLELD");
  pdfFormObject["geographyPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("geographyIEP");
  pdfFormObject["geographyPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("geographyFrench");
  pdfFormObject["geographyPlan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("geographyNA");
  pdfFormObject["geographyPlan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("geographyReport1Mark");
  field.setText(val(pdfFormObject["geographyGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("geographyReport1Median");
  field.setText(val(pdfFormObject["geographyMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("geographyReport2Mark");
  field.setText(val(pdfFormObject["geographyGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("geographyReport2Median");
  field.setText(val(pdfFormObject["geographyMedian2"]));
  field.setFontSize(9);
  field = pdfForm.getField("geographyComments");
  field.setText(val(pdfFormObject["geographyComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // HEALTH EDUCATION
  field = pdfForm.getField("healthEducationESLELD");
  pdfFormObject["healthEducationPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("healthEducationIEP");
  pdfFormObject["healthEducationPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("healthEducationFrench");
  pdfFormObject["healthEducationPlan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("healthEducationReport1Mark");
  field.setText(val(pdfFormObject["healthEducationGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("healthEducationReport1Median");
  field.setText(val(pdfFormObject["healthEducationMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("healthEducationReport2Mark");
  field.setText(val(pdfFormObject["healthEducationGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("healthEducationReport2Median");
  field.setText(val(pdfFormObject["healthEducationMedian2"]));
  field.setFontSize(9);

  // PHYSICAL EDUCATION
  field = pdfForm.getField("physicalEducationESLELD");
  pdfFormObject["physicalEducationPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("physicalEducationIEP");
  pdfFormObject["physicalEducationPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("physicalEducationFrench");
  pdfFormObject["physicalEducationPlan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("physicalEducationReport1Mark");
  field.setText(val(pdfFormObject["physicalEducationGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("physicalEducationReport1Median");
  field.setText(val(pdfFormObject["physicalEducationMedian1"]));
  field.setFontSize(9);
  field = pdfForm.getField("physicalEducationReport2Mark");
  field.setText(val(pdfFormObject["physicalEducationGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("physicalEducationReport2Median");
  field.setText(val(pdfFormObject["physicalEducationMedian2"]));
  field.setFontSize(9);

  field = pdfForm.getField("healthAndPhysicalEducationComments");
  field.setText(val(pdfFormObject["healthAndPhysicalEducationComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // ARTS
  field = pdfForm.getField("artsHeader");
  field.setText(val(pdfFormObject["artsHeader"]));
  field.setFontSize(9);

  // ARTS 1
  field = pdfForm.getField("arts1");
  field.setText(val(pdfFormObject["arts1"]));
  field.setFontSize(8);
  field = pdfForm.getField("arts1French");
  pdfFormObject["arts1Plan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts1ESLELD");
  pdfFormObject["arts1Plan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts1IEP");
  pdfFormObject["arts1Plan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts1NA");
  pdfFormObject["arts1Plan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts1Report1Mark");
  field.setText(val(pdfFormObject["arts1Grade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts1Report1Median");
  field.setText(val(pdfFormObject["arts1Median1"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts1Report2Mark");
  field.setText(val(pdfFormObject["arts1Grade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts1Report2Median");
  field.setText(val(pdfFormObject["arts1Median2"]));
  field.setFontSize(9);

  // ARTS 2
  field = pdfForm.getField("arts2");
  field.setText(val(pdfFormObject["arts2"]));
  field.setFontSize(8);
  field = pdfForm.getField("arts2French");
  pdfFormObject["arts2Plan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts2ESLELD");
  pdfFormObject["arts2Plan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts2IEP");
  pdfFormObject["arts2Plan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts2NA");
  pdfFormObject["arts2Plan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts2Report1Mark");
  field.setText(val(pdfFormObject["arts2Grade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts2Report1Median");
  field.setText(val(pdfFormObject["arts2Median1"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts2Report2Mark");
  field.setText(val(pdfFormObject["arts2Grade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts2Report2Median");
  field.setText(val(pdfFormObject["arts2Median2"]));
  field.setFontSize(9);

  // ARTS 3
  field = pdfForm.getField("arts3");
  field.setText(val(pdfFormObject["arts3"]));
  field.setFontSize(8);
  field = pdfForm.getField("arts3French");
  pdfFormObject["arts3Plan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts3ESLELD");
  pdfFormObject["arts3Plan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts3IEP");
  pdfFormObject["arts3Plan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts3NA");
  pdfFormObject["arts3Plan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts3Report1Mark");
  field.setText(val(pdfFormObject["arts3Grade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts3Report1Median");
  field.setText(val(pdfFormObject["arts3Median1"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts3Report2Mark");
  field.setText(val(pdfFormObject["arts3Grade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts3Report2Median");
  field.setText(val(pdfFormObject["arts3Median2"]));
  field.setFontSize(9);

  // ARTS 4
  field = pdfForm.getField("arts4");
  field.setText(val(pdfFormObject["arts4"]));
  field.setFontSize(8);
  field = pdfForm.getField("arts4French");
  pdfFormObject["arts4Plan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts4ESLELD");
  pdfFormObject["arts4Plan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts4IEP");
  pdfFormObject["arts4Plan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts4NA");
  pdfFormObject["arts4Plan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts4Report1Mark");
  field.setText(val(pdfFormObject["arts4Grade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts4Report1Median");
  field.setText(val(pdfFormObject["arts4Median1"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts4Report2Mark");
  field.setText(val(pdfFormObject["arts4Grade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("arts4Report2Median");
  field.setText(val(pdfFormObject["arts4Median2"]));
  field.setFontSize(9);

  field = pdfForm.getField("artsComments");
  field.setText(val(pdfFormObject["artsComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // OPTIONAL STUDIES (no median fields in PDF)
  field = pdfForm.getField("optionalStudies");
  field.setText(val(pdfFormObject["optionalStudies"]));
  field.setFontSize(9);
  field = pdfForm.getField("optionalStudiesFrench");
  pdfFormObject["optionalStudiesPlan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("optionalStudiesESLELD");
  pdfFormObject["optionalStudiesPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("optionalStudiesIEP");
  pdfFormObject["optionalStudiesPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("optionalStudiesNA");
  pdfFormObject["optionalStudiesPlan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("optionalStudiesReport1Mark");
  field.setText(val(pdfFormObject["optionalStudiesGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("optionalStudiesReport2Mark");
  field.setText(val(pdfFormObject["optionalStudiesGrade2"]));
  field.setFontSize(9);
  field = pdfForm.getField("optionalStudiesComments");
  field.setText(val(pdfFormObject["optionalStudiesComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // ── PAGE 4: Curriculum Expectations ───────────────────────────────────────────────────────────

  field = pdfForm.getField("studentName4");
  field.setText(val(pdfFormObject["studentName"]));
  field.setFontSize(9);
  field = pdfForm.getField("oen4");
  field.setText(val(pdfFormObject["oen"]));
  field.setFontSize(9);
  field = pdfForm.getField("grade4");
  field.setText(val(pdfFormObject["grade"]));
  field.setFontSize(9);


  // TODO (unsupported): teacherSignature, principalSignature

  const __unsupportedHL = collectUnsupportedHighlights(pdfDoc, pdfForm);
  pdfForm.flatten();

  if (highlightRect) {
    const { pageIndex, x, y, width, height } = highlightRect;
    const pages = pdfDoc.getPages();
    if (pages[pageIndex]) {
      pages[pageIndex].drawRectangle({
        x: x - 2,
        y: y - 2,
        width: width + 4,
        height: height + 4,
        color: rgb(0, 0.55, 0),
        opacity: 0.25,
        borderColor: rgb(0.9, 0.7, 0),
        borderWidth: 0,
        borderOpacity: 0.9,
      });
    }
  }

  drawUnsupportedHighlights(pdfDoc, __unsupportedHL);

  // Class logos: drawn last so they sit on top of the flattened template.
  await drawLogos(pdfDoc, logos);

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const filledPDFUrl = URL.createObjectURL(blob);
  return filledPDFUrl;
};

export { fill };
